import { useState, useEffect } from 'react';

import { APP_LINK_LIST_URL } from './deepLink';

import type { AppLinkMap } from './deepLink';

let cachedData: AppLinkMap | null = null;
// SAL-1226: 동시 마운트 시 중복 fetch 방지를 위한 in-flight promise 공유.
let pendingPromise: Promise<AppLinkMap> | null = null;

// SAL-1225: 일시적 CDN/네트워크 오류 시 세션 동안 marketing 자동 오픈이 영구 비활성화되는 것을 방지.
const MAX_RETRY = 1; // 1회 재시도 (총 2회 시도)
const RETRY_DELAY_MS = 1000;

async function fetchAppLinkMapWithRetry(retries = MAX_RETRY): Promise<AppLinkMap> {
  try {
    const response = await fetch(APP_LINK_LIST_URL);
    if (!response.ok) throw new Error('Failed to fetch app link list');
    return await response.json();
  } catch (error) {
    if (retries > 0) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      return fetchAppLinkMapWithRetry(retries - 1);
    }
    throw error;
  }
}

export default function useAppLinkMap() {
  const [appLinkMap, setAppLinkMap] = useState<AppLinkMap | undefined>(cachedData ?? undefined);
  const [isLoading, setIsLoading] = useState(!cachedData);

  useEffect(() => {
    if (cachedData) return;

    let cancelled = false;

    // SAL-1226: 동시 마운트 시 중복 fetch 방지를 위해 in-flight promise 공유.
    if (!pendingPromise) {
      pendingPromise = fetchAppLinkMapWithRetry().finally(() => {
        pendingPromise = null;
      });
    }

    pendingPromise
      .then((data) => {
        if (cancelled) return;
        cachedData = data;
        setAppLinkMap(data);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { appLinkMap, isLoading };
}
